import { Placeholders } from "../../../constants/placeholders";
import { BoardMediaPlaceholderRender } from "../../../util/placeholders/board-media-placeholder-render";
import { BoardMediaImageErrorForm } from "./board-media-image-error-form";

class BoardMediaImageErrorRender {
    private readonly form: BoardMediaImageErrorForm;

    private toPostId(rawPostId: string | null | undefined): number | null {
        const normalizedPostId = Number(rawPostId);
        return Number.isInteger(normalizedPostId) && normalizedPostId > 0 ? normalizedPostId : null;
    }

    public constructor() {
        this.form = new BoardMediaImageErrorForm();
    }

    public replaceBrokenImage(img: HTMLImageElement): void {
        if (!(img instanceof HTMLImageElement)) return;
        if (img.dataset.boardMediaImageFallbackDone === "1") return;

        const mediaArea = this.form.getBoardMediaArea(img);
        if (!(mediaArea instanceof HTMLElement)) return;

        const postCard = this.form.getPostCard(mediaArea);
        const eventRoot = postCard?.parentElement ?? postCard ?? mediaArea;
        const tile = this.form.getSnsPreviewTile(img);
        const tilesGrid = this.form.getSnsPreviewTilesGrid(img);
        const grid = this.form.getBoardImagesGrid(img);

        // data-can-recommend is the same admin/editor gate used sitewide
        // (UserRole::isPrivilegedRole) — reusing it here instead of a separate
        // client-side check.
        const isAdminEditor = postCard instanceof HTMLElement && postCard.dataset.canRecommend === "1";

        if (tile instanceof HTMLElement) {
            img.dataset.boardMediaImageFallbackDone = "1";
            tile.remove();

            if (tilesGrid instanceof HTMLElement) {
                tilesGrid.setAttribute("data-count", String(tilesGrid.children.length));
                if (tilesGrid.children.length === 0) {
                    tilesGrid.remove();
                }
            }

            this.ensureBrokenMediaStateIfNeeded(mediaArea, eventRoot, isAdminEditor);
            return;
        }

        img.dataset.boardMediaImageFallbackDone = "1";
        img.remove();

        if (grid instanceof HTMLElement) {
            grid.setAttribute("data-count", String(this.form.getImages(grid).length));
            if (this.form.getImages(grid).length === 0) {
                grid.remove();
            }
        }

        this.ensureBrokenMediaStateIfNeeded(mediaArea, eventRoot, isAdminEditor);
    }

    private ensureBrokenMediaStateIfNeeded(mediaArea: HTMLElement, eventRoot: HTMLElement, isAdminEditor: boolean): void {
        if (this.form.getRenderableMedia(mediaArea).length > 0) {
            return;
        }

        const placeholder = this.ensureBrokenMediaPlaceholder(mediaArea, isAdminEditor);
        if (!(placeholder instanceof HTMLElement)) {
            return;
        }

        document.dispatchEvent(
            new CustomEvent("app:content:inserted", {
                detail: { root: eventRoot },
            }),
        );
    }

    private ensureBrokenMediaPlaceholder(mediaArea: HTMLElement, isAdminEditor: boolean): HTMLElement | null {
        const existing = this.form.getPlaceholder(mediaArea);
        // This path only runs once every image/video SSR actually rendered for this
        // post has 404'd — the post DOES have attached_images/attached_videos data,
        // unlike the genuinely-no-media TEXT_ONLY case (sns-preview-manager-handler.ts),
        // so it's a broken media reference, not a text-only post.
        const loadingText = isAdminEditor ? Placeholders.MEDIA_404 : Placeholders.TEXT_ONLY;
        const placeholder =
            existing instanceof HTMLElement
                ? existing
                : BoardMediaPlaceholderRender.createLoadingPlaceholder({
                      postId: this.toPostId(mediaArea.dataset.postId),
                      loadingText,
                  });

        if (!(existing instanceof HTMLElement)) {
            mediaArea.prepend(placeholder);
        }

        if (!isAdminEditor) {
            // Not someone who'd act on a broken media reference — blank, same as a
            // post with genuinely no media (data-placeholder-variant="empty", see
            // board-placeholder.blade.php / _placeholder.scss), not a misleading
            // "TEXT ONLY" label.
            placeholder.dataset.placeholderVariant = "empty";
            placeholder.replaceChildren();
            return placeholder;
        }

        // Admin/editor: a distinct "404" indicator so the broken reference is
        // visible and actionable, instead of silently reading as a normal
        // text-only post.
        placeholder.dataset.placeholderVariant = "media-error";

        const text = this.form.getPlaceholderText(placeholder);
        if (text instanceof HTMLElement) {
            text.textContent = Placeholders.MEDIA_404;
            text.classList.remove("loading-placeholder");
            text.classList.add("final-placeholder");
        }

        const icon = this.form.getPlaceholderIcon(placeholder);
        if (icon instanceof HTMLElement) {
            icon.classList.remove("loading-animation");
            icon.classList.add("text-icon");
            icon.innerHTML = '<i class="bi bi-exclamation-triangle-fill"></i>';
        }

        return placeholder;
    }
}

export { BoardMediaImageErrorRender };
